import cn from 'classnames';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, Icon, Popover, Spinner } from '@/shared/ui';

import styles from './AlbumPopover.module.scss';

type AlbumPopoverProps = {
  canEditAlbums: boolean | undefined;
  isDownloading: boolean;
  isDeleting: boolean;
  className?: string;
  testID?: string;
  onEditAlbum: () => void;
  onDownloadAlbum: () => Promise<void>;
  onDeleteAlbum: () => void;
};

export const AlbumPopover: FC<AlbumPopoverProps> = (props) => {
  const {
    isDownloading,
    isDeleting,
    canEditAlbums,
    className,
    testID,
    onEditAlbum,
    onDownloadAlbum,
    onDeleteAlbum,
  } = props;

  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const handleEditAlbum = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEditAlbum();
    setIsOpen(false);
  };

  const handleDownloadAlbum = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isDownloading) {
      await onDownloadAlbum();
    }
  };

  const handleDeleteAlbum = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDeleteAlbum();
    setIsOpen(false);
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className={className}
      testID={testID}
      popoverClassName={styles.popover}
      iconClassName={cn(styles.detailsIcon, isOpen && styles.detailsIconActive)}
    >
      {canEditAlbums && (
        <Button
          onClick={handleEditAlbum}
          variant="tertiary"
          size="small"
          className={styles.popoverButton}
        >
          <Icon name="pen" size={24} />
          {formatMessage({
            id: 'global.edit',
            defaultMessage: 'Edit',
          })}
        </Button>
      )}

      <Button
        onClick={handleDownloadAlbum}
        variant="tertiary"
        size="small"
        className={styles.popoverButton}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <Spinner size={18} className={styles.downloadSpinner} />
        ) : (
          <Icon name="download" size={24} />
        )}
        {formatMessage({
          id: 'global.download',
          defaultMessage: 'Download',
        })}
      </Button>

      {canEditAlbums && (
        <Button
          onClick={handleDeleteAlbum}
          variant="tertiary"
          size="small"
          danger
          className={styles.popoverButton}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Spinner size={18} className={styles.deleteSpinner} />
          ) : (
            <Icon name="delete" size={24} />
          )}
          {formatMessage({
            id: 'global.remove',
            defaultMessage: 'Remove',
          })}
        </Button>
      )}
    </Popover>
  );
};
